/**
 * ProposalReviewerPanel — Phase 4e.G (Feb 2026)
 *
 * Inbox aller pending KI-Vorschläge (job-übergreifend). Pro Artikel die jüngste
 * Proposal mit Hersteller, Anzahl Felder, „Review"-Button (öffnet
 * `AiEnrichArticleModal`) und „Verwerfen"-Button.
 *
 * Schließt den KI-Loop: nach Batch-Lauf hat der Händler hier eine
 * konsolidierte Inbox statt jeden Artikel manuell zu suchen.
 */
import { useEffect, useState } from "react";
import { Sparkles, RefreshCw, X, Loader2, Inbox, Zap } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import AiEnrichArticleModal from "./AiEnrichArticleModal";

export default function ProposalReviewerPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reviewArticle, setReviewArticle] = useState(null);
    const [smartApplying, setSmartApplying] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/articles/ai-enrich/pending-proposals");
            setItems(r.data.pending || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Inbox konnte nicht geladen werden");
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const dismiss = async (articleId) => {
        if (!window.confirm("Diesen KI-Vorschlag verwerfen?")) return;
        try {
            await api.delete(`/articles/${articleId}/ai-enrich/proposals`);
            toast.success("Vorschlag verworfen");
            setItems((arr) => arr.filter((x) => x.article.id !== articleId));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verwerfen fehlgeschlagen");
        }
    };

    const smartApply = async () => {
        if (items.length === 0) return;
        if (!window.confirm(`Smart-Apply: KI-Vorschläge mit ≥60% Vertrauen automatisch übernehmen (${items.length} Kandidaten)?`)) return;
        setSmartApplying(true);
        try {
            const r = await api.post("/articles/ai-enrich/smart-apply", {
                article_ids: items.map((i) => i.article.id),
                threshold: 0.6,
            });
            toast.success(`Smart-Apply: ${r.data.applied} automatisch übernommen, ${r.data.skipped} bleiben in Inbox (manuell)`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Smart-Apply fehlgeschlagen");
        }
        setSmartApplying(false);
    };

    return (
        <div className="space-y-3" data-testid="proposal-reviewer-panel">
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-900">
                <strong>Inbox:</strong> Hier landen alle Artikel mit pending KI-Vorschlägen (z.B. nach einem Batch-Lauf). Klicken Sie auf <em>Review</em>, um pro Artikel Felder einzeln zu übernehmen oder zu verwerfen.
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-gray-600">
                    {items.length} {items.length === 1 ? "Vorschlag" : "Vorschläge"} zur Prüfung
                </div>
                <div className="flex items-center gap-2">
                    {items.length > 0 && (
                        <Button
                            onClick={smartApply}
                            disabled={smartApplying}
                            className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                            data-testid="proposal-reviewer-smart-apply"
                            title="Übernimmt automatisch alle Vorschläge mit ≥60% Vertrauen (EAN + Hersteller + Teilenr.-Match)"
                        >
                            {smartApplying ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                            Smart-Apply
                        </Button>
                    )}
                    <Button onClick={load} variant="outline" className="h-8 rounded-sm text-xs" data-testid="proposal-reviewer-refresh">
                        <RefreshCw className="w-3.5 h-3.5 mr-1" />
                        Aktualisieren
                    </Button>
                </div>
            </div>

            {loading && <div className="text-sm text-gray-500 py-4 text-center"><Loader2 className="w-5 h-5 inline animate-spin" /></div>}

            {!loading && items.length === 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-sm py-8 text-center" data-testid="proposal-reviewer-empty">
                    <Inbox className="w-8 h-8 mx-auto text-gray-300" />
                    <div className="mt-2 text-sm text-gray-500">Inbox leer.</div>
                    <div className="text-xs text-gray-400 mt-1">Starten Sie einen Batch-KI-Lauf um Vorschläge zu generieren.</div>
                </div>
            )}

            {!loading && items.length > 0 && (
                <div className="space-y-2" data-testid="proposal-reviewer-list">
                    {items.map((it) => (
                        <div key={it.proposal_id} className="bg-white border border-gray-200 rounded-sm p-3 flex items-center gap-3" data-testid={`proposal-row-${it.article.id}`}>
                            <Sparkles className="w-5 h-5 text-[#FFCC00] shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm truncate">{it.article.name}</div>
                                <div className="text-xs text-gray-500 mt-0.5 font-mono-num">
                                    SKU: {it.article.sku}
                                    {it.article.manufacturer && <> · {it.article.manufacturer}</>}
                                    {" · "}
                                    <span className="text-emerald-700 font-bold">{it.fields_count} Vorschläge</span>
                                </div>
                            </div>
                            <Button
                                onClick={() => setReviewArticle(it.article)}
                                className="h-8 rounded-sm bg-[#111827] hover:bg-black text-white text-xs"
                                data-testid={`proposal-review-${it.article.id}`}
                            >
                                Review
                            </Button>
                            <button
                                type="button"
                                onClick={() => dismiss(it.article.id)}
                                className="p-1.5 hover:bg-red-50 rounded-sm text-red-600"
                                title="Verwerfen"
                                data-testid={`proposal-dismiss-${it.article.id}`}
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {reviewArticle && (
                <AiEnrichArticleModal
                    article={reviewArticle}
                    onClose={() => setReviewArticle(null)}
                    onApplied={() => {
                        // Nach Apply lädt das Modal den Article neu, ENTfernt die Proposal serverseitig.
                        // Wir entfernen den Eintrag auch lokal.
                        setItems((arr) => arr.filter((x) => x.article.id !== reviewArticle.id));
                        setReviewArticle(null);
                    }}
                />
            )}
        </div>
    );
}
